import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { Badge, Button, Buttons, Icon, Tag } from "capitalrx-components-2.0/lib/general";
import { Checkbox, CheckboxValue, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Subheading } from "capitalrx-components-2.0/lib/typography";

import { PageLayout } from "@/premium/themedComponents";
import { Card } from "@/premium/themedComponents/Card";
import { EnrollmentContext } from "@/state/context";
import { PlanCard } from "@/common/components";
import { useNavigateSearch } from "@/premium/hooks";
import { StackOrShelf } from "@/common/components/StackOrShelf";
import { useTranslation } from "react-i18next";
import { Breadcrumbs } from "@/premium/themedComponents/Breadcrumbs";
import { NoResultsSection } from "./NoResultsSection";
import "./FilterDropdown.scss";

export const PlansPage = () => {
  const [state, _] = useContext(EnrollmentContext);
  const { t: translate } = useTranslation();
  const navigateSearch = useNavigateSearch();
  const plans = state.experienceData.plan_csids;
  const planFilters = state.experienceData.plan_filters ?? [];
  const { columns } = useResponsive({
    columns: {
      mobile: 12,
      tablet: 6,
      desktop: 6,
    },
  });

  const [selectedFilterIds, setSelectedFilterIds] = useState<number[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterSearch, setFilterSearch] = useState("");
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isFilterOpen) return undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isFilterOpen]);

  const handlePlanCardClick = (id: number) => {
    navigateSearch("/plans/plan-details", {
      enrollmentPlan: id,
    });
  };

  const filtersByPlanCsid = useMemo(() => {
    const map: Record<string, string[]> = {};
    for (const plan of plans ?? []) {
      map[plan.coverage_strategy_id] = planFilters
        .filter((f) => f.plan_benefit_csids.includes(plan.coverage_strategy_id))
        .map((f) => f.display_name);
    }
    return map;
  }, [plans, planFilters]);

  const visiblePlans = useMemo(() => {
    if (selectedFilterIds.length === 0) return plans ?? [];
    const selectedFilters = planFilters.filter((f) => selectedFilterIds.includes(f.id));
    return (plans ?? []).filter((plan) =>
      selectedFilters.some((f) => f.plan_benefit_csids.includes(plan.coverage_strategy_id)),
    );
  }, [plans, planFilters, selectedFilterIds]);

  const relevantFilters = useMemo(() => {
    const planCsids = new Set((plans ?? []).map((p) => p.coverage_strategy_id));
    return planFilters.filter((f) =>
      f.plan_benefit_csids.some((csid) => planCsids.has(csid)),
    );
  }, [plans, planFilters]);

  const filteredDropdownOptions = useMemo(() => {
    if (!filterSearch) return relevantFilters;
    const search = filterSearch.toLowerCase();
    return relevantFilters.filter((f) => f.display_name.toLowerCase().includes(search));
  }, [relevantFilters, filterSearch]);

  const handleFilterToggle = useCallback((filterId: number, checked: CheckboxValue) => {
    setSelectedFilterIds((prev) =>
      checked === true ? [...prev, filterId] : prev.filter((id) => id !== filterId),
    );
  }, []);

  const handleRemoveBadge = useCallback((filterId: number) => {
    setSelectedFilterIds((prev) => prev.filter((id) => id !== filterId));
  }, []);

  const handleClearAll = useCallback(() => {
    setSelectedFilterIds([]);
  }, []);

  const hasActiveFilters = selectedFilterIds.length > 0;

  const filterButtonAppearance = hasActiveFilters || isFilterOpen
    ? Buttons.Functional.Secondary.Large
    : Buttons.Functional.Tertiary.Large;

  return (
    <PageLayout showGradient={false}>
      <Stack>
        <Breadcrumbs />
        <Stack spacing="small">
          <Heading>{translate("PLAN_PAGE.plans")}</Heading>
          <Body>{translate("PLAN_PAGE.planPageDescription")}</Body>
        </Stack>
        {relevantFilters.length > 0 && (
          <Stack spacing="small">
            <StackOrShelf>
              <div className="plans-filter-dropdown" ref={filterDropdownRef}>
                <Button
                  appearance={filterButtonAppearance}
                  icon="filter"
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                >
                  {translate("PLAN_PAGE.filter")}
                </Button>
                {isFilterOpen && (
                  <div className="plans-filter-dropdown-menu">
                    <Card>
                      <Stack spacing="small">
                        <Textbox
                          value={filterSearch}
                          onChange={(value) => setFilterSearch(value ?? "")}
                          placeholder={translate("PLAN_PAGE.search")}
                          prefix={<Icon name="search" />}
                        />
                        <Stack spacing="mini">
                          {filteredDropdownOptions.map((filter) => (
                            <Checkbox
                              key={filter.id}
                              label={filter.display_name}
                              value={selectedFilterIds.includes(filter.id)}
                              onChange={(checked) => handleFilterToggle(filter.id, checked)}
                            />
                          ))}
                        </Stack>
                      </Stack>
                    </Card>
                  </div>
                )}
              </div>
              <Shelf.Spacer />
            </StackOrShelf>
            {hasActiveFilters && (
              <Stack spacing="mini">
                <Shelf spacing="mini" responsive="wrap" responsiveAlignment="left">
                  {selectedFilterIds.map((filterId) => {
                    const filter = planFilters.find((f) => f.id === filterId);
                    return filter ? (
                      <Badge key={filterId} onClick={() => handleRemoveBadge(filterId)}>
                        {filter.display_name}
                      </Badge>
                    ) : null;
                  })}
                </Shelf>
                <Button appearance="text" onClick={handleClearAll}>{translate("PLAN_PAGE.clearAll")}</Button>
              </Stack>
            )}
          </Stack>
        )}
        <Grid>
          {
            visiblePlans.length > 0 && visiblePlans.map((plan) => {
              return (
                <Grid.Span columns={columns} key={plan.rn}>
                  <PlanCard onClick={() => handlePlanCardClick(plan.id)}>
                    <Stack spacing="mini">
                      <Subheading>{plan.display_name}</Subheading>
                      <Shelf spacing="mini" responsive="wrap" responsiveAlignment="left">
                        {
                          (filtersByPlanCsid[plan.coverage_strategy_id] ?? []).map((filterName, index) => (
                            <Tag key={`${filterName}-${index}`}>{filterName}</Tag>
                          ))
                        }
                      </Shelf>
                    </Stack>
                  </PlanCard>
                </Grid.Span>
              );
            })
          }
        </Grid>
        {visiblePlans.length === 0 && hasActiveFilters && (
          <NoResultsSection message={translate("PLAN_PAGE.noResults")} />
        )}
        {(plans?.length ?? 0) === 0 && (
          <NoResultsSection message={translate("PLAN_PAGE.noPlans")} />
        )}
      </Stack>
    </PageLayout>
  );
};
